import React, { useRef, useEffect, useState } from 'react';
import { CATEGORIES, HIGGS_RELATIONS } from '../data';
import { RUACH_PARTICLES } from '../ruach';

export default function ShareableCard({ particle, mode, onClose }) {
  const canvasRef = useRef(null);
  const [generated, setGenerated] = useState(false);
  const isRuach = mode === 'ruach';

  const cat = CATEGORIES[particle.category] || CATEGORIES.boson;
  const rel = HIGGS_RELATIONS[particle.higgsRelation] || HIGGS_RELATIONS.none;
  const ruach = RUACH_PARTICLES[particle.id];
  const accentColor = isRuach ? '#ffb060' : '#f0c040';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;

    // Background
    ctx.fillStyle = isRuach ? '#0a0604' : '#060810';
    ctx.fillRect(0, 0, W, H);

    // Animated field dots (static for card)
    for (let i = 0; i < 120; i++) {
      const seed = i * 137.5;
      const x = (seed * 2.3) % W;
      const y = (seed * 1.7) % H;
      ctx.beginPath();
      ctx.arc(x, y, 0.8 + (i % 3) * 0.6, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${isRuach ? '255,160,60' : '240,192,64'},${0.04 + (i % 5) * 0.02})`;
      ctx.fill();
    }

    // Grid lines (subtle)
    ctx.strokeStyle = isRuach ? 'rgba(255,160,60,0.04)' : 'rgba(240,192,64,0.04)';
    ctx.lineWidth = 0.5;
    for (let x = 0; x < W; x += 40) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
    }
    for (let y = 0; y < H; y += 40) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    }

    // Top accent bar
    ctx.fillStyle = accentColor;
    ctx.fillRect(0, 0, W, 3);

    // Category pill
    ctx.fillStyle = cat.bg || cat.color + '22';
    roundRect(ctx, 24, 24, 100, 24, 12);
    ctx.fill();
    ctx.fillStyle = cat.color;
    ctx.font = '600 10px "Space Mono", monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(cat.label.toUpperCase(), 34, 36);

    // Mode badge
    ctx.fillStyle = `${accentColor}22`;
    const modeText = isRuach ? 'RUACH · רוּחַ' : 'SCIENCE';
    const modeW = 90;
    roundRect(ctx, W - 24 - modeW, 24, modeW, 24, 12);
    ctx.fill();
    ctx.fillStyle = accentColor;
    ctx.font = '600 9px "Space Mono", monospace';
    ctx.textAlign = 'center';
    ctx.fillText(modeText, W - 24 - modeW / 2, 36);

    // Big symbol
    const symColor = cat.color;
    ctx.font = 'bold 72px "Space Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = symColor + '18';
    ctx.fillText(particle.symbol, W / 2 + 2, 130 + 2);
    ctx.fillStyle = symColor;
    ctx.fillText(particle.symbol, W / 2, 130);

    // Glow behind symbol
    const grd = ctx.createRadialGradient(W / 2, 130, 0, W / 2, 130, 70);
    grd.addColorStop(0, symColor + '20');
    grd.addColorStop(1, symColor + '00');
    ctx.beginPath(); ctx.arc(W / 2, 130, 70, 0, Math.PI * 2);
    ctx.fillStyle = grd; ctx.fill();

    // Particle name
    ctx.font = 'bold 22px "Syne", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(isRuach && ruach ? ruach.ruachName : particle.name, W / 2, 180);

    // Hebrew name in Ruach mode
    if (isRuach && ruach?.ruachHebrew) {
      ctx.font = '16px serif';
      ctx.fillStyle = accentColor + 'aa';
      ctx.fillText(ruach.ruachHebrew, W / 2, 208);
    }

    // Divider
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(24, isRuach && ruach?.ruachHebrew ? 232 : 218);
    ctx.lineTo(W - 24, isRuach && ruach?.ruachHebrew ? 232 : 218);
    ctx.stroke();

    const yBase = isRuach && ruach?.ruachHebrew ? 248 : 234;

    // Stats grid
    const stats = [
      { label: 'Mass', value: particle.massLabel },
      { label: 'Spin', value: particle.spin },
      { label: 'Charge', value: particle.charge },
      { label: isRuach ? 'Ruach' : 'Higgs', value: rel.short },
    ];

    stats.forEach((s, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = col === 0 ? 24 : W / 2 + 12;
      const y = yBase + row * 52;
      const boxW = W / 2 - 36;

      ctx.fillStyle = 'rgba(255,255,255,0.04)';
      roundRect(ctx, x, y, boxW, 42, 6);
      ctx.fill();

      ctx.font = '9px "Space Mono", monospace';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.fillText(s.label.toUpperCase(), x + 10, y + 8);

      ctx.font = 'bold 13px "Space Mono", monospace';
      ctx.fillStyle = i === 3 ? rel.color : '#ffffff';
      ctx.fillText(s.value, x + 10, y + 22);
    });

    // Description snippet
    const desc = isRuach && ruach ? ruach.description : particle.description;
    const snippetY = yBase + 120;
    ctx.font = '11px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    const words = desc.split(' ');
    let line = '', lineY = snippetY;
    const maxW = W - 48, lineH = 17;
    for (let i = 0; i < words.length && lineY < H - 60; i++) {
      const test = line + (line ? ' ' : '') + words[i];
      if (ctx.measureText(test).width > maxW && line) {
        ctx.fillText(line, 24, lineY);
        line = words[i]; lineY += lineH;
      } else { line = test; }
    }
    if (line && lineY < H - 60) ctx.fillText(line + '...', 24, lineY);

    // Bottom branding
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    ctx.fillRect(0, H - 42, W, 42);
    ctx.font = 'bold 11px "Space Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = accentColor;
    ctx.fillText('higgs-model.vercel.app', W / 2, H - 21);

    setGenerated(true);
  }, [particle, mode]);

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  const handleDownload = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `${particle.id}-${mode}-card.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1.5rem',
    }}>
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
        <canvas ref={canvasRef} width={360} height={520}
          style={{ borderRadius: '16px', maxWidth: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.8)' }} />

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={handleDownload} style={{
            padding: '0.7rem 1.5rem', borderRadius: '8px', cursor: 'pointer',
            background: `${isRuach ? '#ffb060' : '#f0c040'}25`,
            border: `1px solid ${isRuach ? '#ffb06050' : '#f0c04050'}`,
            color: isRuach ? '#ffb060' : '#f0c040',
            fontFamily: 'Space Mono', fontSize: '0.78rem', letterSpacing: '0.08em',
          }}>
            Download card
          </button>
          <button onClick={onClose} style={{
            padding: '0.7rem 1.25rem', borderRadius: '8px', cursor: 'pointer',
            background: 'none', border: '1px solid rgba(255,255,255,0.15)',
            color: 'rgba(255,255,255,0.5)', fontFamily: 'Space Mono', fontSize: '0.75rem',
          }}>
            Close
          </button>
        </div>
        <p style={{ fontSize: '0.68rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.25)', textAlign: 'center' }}>
          Right-click the card to save, or click Download
        </p>
      </div>
    </div>
  );
}
